import { Modal, ModalProps, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'

import {
  usePersonHistoriesServiceGetPersonHistoryListEndpoint,
  useClassifierServiceGetCountryClassifierElementListEndpoint
} from 'src/api/queries'
import { GetPersonHistoryListResponseItem } from 'src/api/requests'
import { useTablePagination } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormat,
  dayMonthYearFormatWithDot
} from 'src/libs'
import { useLocales } from 'src/locales'

import { GenderShortMap } from '../../utils'

type Props = {
  onCancel: () => void
} & ModalProps

export const HistoryModal = ({ onCancel, ...props }: Props) => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()

  const pagination = useTablePagination()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    personId: String(id)
  }

  const { data, isLoading } =
    usePersonHistoriesServiceGetPersonHistoryListEndpoint(queryParams)

  const { data: countries } =
    useClassifierServiceGetCountryClassifierElementListEndpoint()

  const columns: ColumnsType<GetPersonHistoryListResponseItem> = [
    {
      key: 'changed at',
      title: t('epidemiology.updated_date'),
      render: (_, { changedAt }) =>
        dayjs.utc(changedAt).tz().format(dayMonthYearHourMinutesFormat)
    },
    {
      key: 'last name',
      title: t('form.last_name'),
      dataIndex: 'lastName'
    },
    {
      key: 'first name',
      title: t('form.first_name'),
      dataIndex: 'firstName'
    },
    {
      key: 'idnp',
      title: t('form.idnp'),
      dataIndex: 'idnp'
    },
    {
      key: 'birth date',
      title: t('form.birth_date'),
      render: (_, { birthDate }) =>
        birthDate ? dayjs(birthDate).format(dayMonthYearFormatWithDot) : '-'
    },
    {
      key: 'phone',
      title: t('common.phone'),
      dataIndex: 'phone'
    },
    {
      key: 'gender',
      title: t('form.gender'),
      render: (_, { gender }) => (gender ? GenderShortMap[gender] : '-')
    },
    {
      key: 'email',
      title: t('form.email'),
      dataIndex: 'email'
    },
    {
      key: 'social status',
      title: t('form.social_status'),
      dataIndex: ['socialStatus', 'translation', currentLang?.value]
    },
    {
      key: 'citizenship',
      title: t('common.citizenship'),
      render: (_, { citizenship }) =>
        countries?.data?.find((country) => country.isoCode === citizenship)?.[
          currentLang?.value
        ]
    },
    {
      key: 'address',
      title: t('other_institutions.address'),
      render: (_, { currentAddress }) =>
        [
          currentAddress?.cuatmLocality?.translation?.[currentLang?.value],
          currentAddress?.street,
          currentAddress?.streetNumber,
          currentAddress?.apartmentNumber
        ]
          .filter(Boolean)
          .join(', ')
    },
    {
      key: 'anonymous',
      title: t('epidemiology.patient_status.anonymous'),
      render: (_, { isAnonymous }) =>
        isAnonymous ? t('common.yes') : t('common.no')
    },
    {
      key: 'investigation status',
      title: t('form.status'),
      render: (_, { investigationStatus }) =>
        investigationStatus && t(`persons.${investigationStatus}`)
    },
    {
      key: 'institution',
      title: t('common.institution'),
      dataIndex: ['institution', 'translation', currentLang?.value]
    },
    {
      key: 'testrap code',
      title: t('common.testrap_code'),
      dataIndex: 'testrapCode'
    },
    {
      key: 'sime code',
      title: t('common.sime_code'),
      dataIndex: 'simeCode'
    }
  ]

  return (
    <Modal
      open
      width={1000}
      maskClosable={false}
      title={t('persons.history_modal_title')}
      footer={null}
      onCancel={onCancel}
      {...props}
    >
      <Table
        bordered
        dataSource={data?.data}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </Modal>
  )
}
